/**
 * Lo que el comerciante ve de los terminos de cobertura.
 *
 * Mike, 9-oct: "must only show paragraph that states by first use of services
 * I agree with all terms and conditions as described. No lawyer references or
 * any other references other than time and date."
 *
 * Asi que la pantalla muestra el texto publicado y, si ya lo acepto, la fecha y
 * la hora. Nada mas: ni version, ni hash, ni casilla. La aceptacion se registra
 * en el backend con el primer uso (`aceptar_por_primer_uso`), y la version y el
 * hash siguen guardados en cada aceptacion aunque aca no se vean.
 *
 * Lo que no se finge: si no hay texto publicado, lo dice en vez de mostrar uno.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { apiErrorMessage } from '../lib/apiError';
import { ShieldCheck, Loader2, AlertCircle, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fecha = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC',
    }) + ' UTC';
  } catch (e) {
    return iso;
  }
};

const CoverageTermsPage = () => {
  const { getAuthHeaders } = useAuth();

  const [terminos, setTerminos] = useState(null);
  const [sinTerminos, setSinTerminos] = useState(false);
  const [mia, setMia] = useState(null);
  const [alDia, setAlDia] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [rt, ra] = await Promise.all([
        fetch(`${API}/insurance/terms`, { headers: getAuthHeaders() }),
        fetch(`${API}/insurance/acceptance/mine`, { headers: getAuthHeaders() }),
      ]);

      if (rt.status === 503) {
        setSinTerminos(true);
        setTerminos(null);
      } else if (rt.ok) {
        setSinTerminos(false);
        setTerminos(await rt.json());
      } else {
        // readApiError espera (status, body) y devuelve un objeto: pasarle la
        // respuesta entera dejaba un objeto en `error` y la pagina se caia.
        setError(apiErrorMessage(await rt.json().catch(() => null), 'The terms could not be loaded.'));
      }

      if (ra.ok) {
        const d = await ra.json();
        setMia(d.acceptance || null);
        setAlDia(Boolean(d.up_to_date));
      }
    } catch (e) {
      setError('Could not reach the server.');
    } finally {
      setCargando(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading terms
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5 p-1" data-testid="coverage-terms-page">
      <h1 className="text-2xl font-semibold flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-indigo-600" /> Terms and conditions
      </h1>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {sinTerminos && (
        <Card>
          <CardContent className="py-8 text-center">
            <FileText className="h-8 w-8 mx-auto text-slate-400" />
            <p className="mt-3 font-medium">No terms have been published yet.</p>
          </CardContent>
        </Card>
      )}

      {terminos && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="terms-text">
              {terminos.text}
            </p>
            {mia && alDia && (
              <p className="mt-4 text-sm text-slate-500" data-testid="terms-accepted-at">
                Accepted {fecha(mia.accepted_at)}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CoverageTermsPage;
